// =============================================================================
// PAGE SCANNER MOBILE AUTONOME
// =============================================================================
// Page dédiée au scan de documents et photos depuis mobile
// Accessible via /scanner?type=document|photo&dossierId=xxx
// =============================================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import { jsPDF } from "jspdf";
import {
  Camera,
  RotateCcw,
  Check,
  X,
  FlipHorizontal,
  Loader2,
  FileText,
  Image as ImageIcon,
  AlertCircle,
  ChevronLeft,
  Crop,
  Upload,
  Home,
  CheckCircle2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast, Toaster } from "sonner";
import { useConfigList } from "../../hooks/useConfigList";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Types de documents disponibles
const DOCUMENT_TYPES = [
  { value: "piece_identite", label: "Pièce d'identité", icon: FileText },
  { value: "justificatif_domicile", label: "Justificatif de domicile", icon: FileText },
  { value: "avis_imposition", label: "Avis d'imposition", icon: FileText },
  { value: "devis_signe", label: "Devis signé", icon: FileText },
  { value: "mandat_mpr", label: "Mandat MaPrimeRénov'", icon: FileText },
  { value: "attestation_honneur", label: "Attestation sur l'honneur", icon: FileText },
  { value: "rib", label: "RIB", icon: FileText },
  { value: "facture", label: "Facture", icon: FileText },
  { value: "attestation_fin_travaux", label: "Attestation fin de travaux", icon: FileText },
  { value: "photo_avant", label: "Photo avant travaux", icon: ImageIcon },
  { value: "photo_apres", label: "Photo après travaux", icon: ImageIcon },
  { value: "autre", label: "Autre document", icon: FileText },
];

// Catégories de photos — chargées depuis DB via useConfigList

// Compression d'image
const compressImage = (imageSrc, maxWidth = 1200, quality = 0.75) => {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.src = imageSrc;
  });
};

// Conversion en PDF
const imageToPdf = async (imageSrc, filename) => {
  const img = new window.Image();
  
  return new Promise((resolve) => {
    img.onload = () => {
      const pdf = new jsPDF({
        orientation: img.width > img.height ? "landscape" : "portrait",
        unit: "px",
        format: [img.width, img.height]
      });
      
      pdf.addImage(imageSrc, "JPEG", 0, 0, img.width, img.height);
      const pdfBlob = pdf.output("blob");
      resolve(new File([pdfBlob], filename, { type: "application/pdf" }));
    };
    img.src = imageSrc;
  });
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================
export default function ScannerPage() {
  const { items: photoCatItems } = useConfigList("photo_categories");
  const PHOTO_CATEGORIES = photoCatItems.length > 0
    ? photoCatItems.map(c => ({ value: c.id, label: c.label }))
    : [{ value: "autre", label: "Autre" }];
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Paramètres URL
  const scanType = searchParams.get("type") || "document"; // document | photo
  const docType = searchParams.get("docType") || "";
  const photoCategory = searchParams.get("category") || "";
  const dossierId = searchParams.get("dossierId") || "";
  
  // États
  const [step, setStep] = useState("select"); // select | capture | preview | crop | uploading | success
  const [selectedType, setSelectedType] = useState(docType || "");
  const [selectedCategory, setSelectedCategory] = useState(photoCategory || "");
  const [capturedImage, setCapturedImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [uploading, setUploading] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [tenantId, setTenantId] = useState("climatpac");
  const [uploadedCount, setUploadedCount] = useState(0);
  
  // Crop state
  const [cropArea, setCropArea] = useState({ x: 5, y: 5, width: 90, height: 90 });
  
  // Charger le tenant
  useEffect(() => {
    const storedTenant = localStorage.getItem("tenantId") || "climatpac";
    setTenantId(storedTenant);
    
    // Si type pré-sélectionné, aller directement à la capture
    if (scanType === "document" && docType) {
      setSelectedType(docType);
      setStep("capture");
    } else if (scanType === "photo" && photoCategory) {
      setSelectedCategory(photoCategory);
      setStep("capture");
    }
  }, [scanType, docType, photoCategory]);
  
  // Config webcam
  const videoConstraints = {
    facingMode: facingMode,
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    aspectRatio: { ideal: 4/3 }
  };
  
  // Capture
  const capturePhoto = useCallback(() => {
    if (!webcamRef.current) return;
    
    const imageSrc = webcamRef.current.getScreenshot({
      width: 1920,
      height: 1080
    });
    
    if (imageSrc) {
      setCapturedImage(imageSrc);
      setStep("preview");
    } else {
      toast.error("Erreur lors de la capture");
    }
  }, []);
  
  // Toggle caméra
  const toggleCamera = () => {
    setFacingMode(prev => prev === "environment" ? "user" : "environment");
    setCameraReady(false);
  };
  
  // Appliquer crop
  const applyCrop = async () => {
    if (!capturedImage || !canvasRef.current) return;
    
    const img = new window.Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      
      const sx = (cropArea.x / 100) * img.width;
      const sy = (cropArea.y / 100) * img.height;
      const sWidth = (cropArea.width / 100) * img.width;
      const sHeight = (cropArea.height / 100) * img.height;
      
      canvas.width = sWidth;
      canvas.height = sHeight;
      
      ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, sWidth, sHeight);
      
      const croppedDataUrl = canvas.toDataURL("image/jpeg", 0.9);
      setCroppedImage(croppedDataUrl);
      setStep("preview");
    };
    img.src = capturedImage;
  };
  
  // Upload
  const uploadDocument = async () => {
    if (!dossierId) {
      toast.error("Aucun dossier sélectionné");
      return;
    }
    
    const imageToUse = croppedImage || capturedImage;
    if (!imageToUse) return;
    
    setUploading(true);
    setStep("uploading");
    
    try {
      const compressedImage = await compressImage(imageToUse, 1200, 0.75);
      const timestamp = new Date().toISOString().slice(0, 10);
      
      let file;
      let endpoint;
      let formData = new FormData();
      
      if (scanType === "document") {
        // Upload document
        const typeLabel = DOCUMENT_TYPES.find(t => t.value === selectedType)?.label || selectedType;
        const baseName = `${selectedType}_${timestamp}`;
        
        // Convertir en PDF pour les documents
        file = await imageToPdf(compressedImage, `${baseName}.pdf`);
        
        formData.append("file", file);
        formData.append("type_document", selectedType);
        formData.append("nom", `${typeLabel} - ${timestamp}`);
        formData.append("source", "scan_mobile");
        
        endpoint = `${API}/api/metier/dossiers/${dossierId}/documents`;
      } else {
        // Upload photo
        const catLabel = PHOTO_CATEGORIES.find(c => c.value === selectedCategory)?.label || selectedCategory;
        const baseName = `photo_${selectedCategory}_${timestamp}`;
        
        // Garder en JPEG pour les photos
        const response = await fetch(compressedImage);
        const blob = await response.blob();
        file = new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
        
        formData.append("file", file);
        formData.append("categorie", selectedCategory);
        formData.append("description", catLabel);
        formData.append("source", "scan_mobile");
        
        endpoint = `${API}/api/metier/dossiers/${dossierId}/photos`;
      }
      
      const token = localStorage.getItem("session_token");
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          ...enteteTenant(tenantId),
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        body: formData
      });
      
      if (res.ok) {
        setUploadedCount(prev => prev + 1);
        toast.success(scanType === "document" ? "Document enregistré" : "Photo enregistrée");
        setStep("success");
      } else {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de l'upload");
      }
    } catch (error) {
      console.error("Erreur upload:", error);
      toast.error(`Échec: ${error.message}`);
      setStep("preview");
    } finally {
      setUploading(false);
    }
  };
  
  // Reprendre
  const retakePhoto = () => {
    setCapturedImage(null);
    setCroppedImage(null);
    setCropArea({ x: 5, y: 5, width: 90, height: 90 });
    setStep("capture");
  };
  
  // Scanner un autre
  const scanAnother = () => {
    setCapturedImage(null);
    setCroppedImage(null);
    setSelectedType("");
    setSelectedCategory("");
    setCropArea({ x: 5, y: 5, width: 90, height: 90 });
    setStep("select");
  };
  
  // Fermer
  const handleClose = () => {
    window.close();
    // Fallback si window.close() ne fonctionne pas
    setTimeout(() => {
      navigate(-1);
    }, 100);
  };

  // ==========================================================================
  // RENDER - SÉLECTION TYPE
  // ==========================================================================
  if (step === "select") {
    const items = scanType === "document" ? DOCUMENT_TYPES : PHOTO_CATEGORIES;
    
    return (
      <div className="min-h-screen bg-slate-900 text-white" data-testid="scanner-page">
        <Toaster position="top-center" theme="dark" />
        
        {/* Header */}
        <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-slate-700 p-4 z-10">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold">
              {scanType === "document" ? "Scanner un document" : "Prendre une photo"}
            </h1>
            <div className="w-10" /> {/* Spacer */}
          </div>
        </div>
        
        {/* Contenu */}
        <div className="max-w-lg mx-auto p-4 space-y-3">
          {!dossierId && (
            <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-200">
                Aucun dossier sélectionné. Retournez sur la fiche dossier pour scanner un document.
              </p>
            </div>
          )}
          
          <p className="text-slate-400 text-sm">
            {scanType === "document" 
              ? "Sélectionnez le type de document à scanner"
              : "Sélectionnez la catégorie de photo"}
          </p>
          
          {items.map((item) => {
            const Icon = item.icon || ImageIcon;
            const value = scanType === "document" ? item.value : item.value;
            const isSelected = scanType === "document" 
              ? selectedType === value 
              : selectedCategory === value;
            
            return (
              <button
                key={value}
                onClick={() => {
                  if (scanType === "document") {
                    setSelectedType(value);
                  } else {
                    setSelectedCategory(value);
                  }
                  if (dossierId) {
                    setStep("capture");
                  } else {
                    toast.error("Veuillez d'abord sélectionner un dossier");
                  }
                }}
                disabled={!dossierId}
                className={`w-full p-4 rounded-xl border transition-all text-left flex items-center gap-3 ${
                  !dossierId
                    ? "bg-slate-800/50 border-slate-700 opacity-50 cursor-not-allowed"
                    : isSelected
                      ? "bg-blue-600/20 border-blue-500"
                      : "bg-slate-800 border-slate-700 hover:border-slate-500"
                }`}
              >
                <div className="p-2 rounded-lg bg-slate-700">
                  <Icon className="w-5 h-5 text-slate-300" />
                </div>
                <span className="flex-1 font-medium">{item.label}</span>
                <ChevronLeft className="w-5 h-5 text-zinc-400 rotate-180" />
              </button>
            );
          })}
        </div>
        
        {/* Footer */}
        {uploadedCount > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur border-t border-slate-700 p-4">
            <div className="max-w-lg mx-auto flex items-center justify-between">
              <span className="text-green-400 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                {uploadedCount} {scanType === "document" ? "document(s)" : "photo(s)"} envoyé(s)
              </span>
              <Button variant="outline" size="sm" onClick={handleClose}>
                Terminer
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==========================================================================
  // RENDER - CAPTURE
  // ==========================================================================
  if (step === "capture") {
    const currentLabel = scanType === "document"
      ? DOCUMENT_TYPES.find(t => t.value === selectedType)?.label
      : PHOTO_CATEGORIES.find(c => c.value === selectedCategory)?.label;
    
    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col">
        <Toaster position="top-center" theme="dark" />
        
        {/* Header overlay */}
        <div className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/80 to-transparent">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={scanAnother}
              className="text-white"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Retour
            </Button>
            <span className="text-white font-medium text-sm">{currentLabel}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleCamera}
              className="text-white"
            >
              <FlipHorizontal className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Caméra */}
        <div className="flex-1 relative flex items-center justify-center bg-black">
          {cameraError ? (
            <div className="text-center p-6">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-white mb-2">Impossible d'accéder à la caméra</p>
              <p className="text-slate-400 text-sm mb-4">{cameraError}</p>
              <Button onClick={() => setCameraError(null)}>Réessayer</Button>
            </div>
          ) : (
            <>
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                onUserMedia={() => setCameraReady(true)}
                onUserMediaError={(err) => setCameraError(err.message || "Accès refusé")}
                className="w-full h-full object-cover"
                style={{ transform: facingMode === "user" ? "scaleX(-1)" : "none" }}
              />
              
              {/* Guide visuel pour documents */}
              {scanType === "document" && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-[85%] aspect-[3/4] border-2 border-white/60 rounded-lg relative">
                    <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg" />
                    <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg" />
                    <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg" />
                  </div>
                </div>
              )}
              
              {/* Guide photo */}
              {scanType === "photo" && (
                <div className="absolute bottom-24 left-0 right-0 text-center pointer-events-none">
                  <span className="bg-black/50 text-white text-sm px-4 py-2 rounded-full">
                    Cadrez votre photo et appuyez sur le bouton
                  </span>
                </div>
              )}
              
              {/* Loader caméra */}
              {!cameraReady && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Bouton capture */}
        <div className="absolute bottom-0 left-0 right-0 z-10 p-6 bg-gradient-to-t from-black/80 to-transparent">
          <div className="flex items-center justify-center">
            <button
              onClick={capturePhoto}
              disabled={!cameraReady}
              className="w-20 h-20 rounded-full bg-zinc-900/50 flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform shadow-lg"
              data-testid="capture-button"
            >
              <div className="w-16 h-16 rounded-full border-4 border-slate-900" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================================================
  // RENDER - PREVIEW
  // ==========================================================================
  if (step === "preview" || step === "uploading") {
    const currentLabel = scanType === "document"
      ? DOCUMENT_TYPES.find(t => t.value === selectedType)?.label
      : PHOTO_CATEGORIES.find(c => c.value === selectedCategory)?.label;
    const imageToShow = croppedImage || capturedImage;
    
    return (
      <div className="fixed inset-0 bg-slate-900 z-50 flex flex-col">
        <Toaster position="top-center" theme="dark" />
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <Button variant="ghost" size="sm" onClick={retakePhoto} disabled={uploading}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reprendre
          </Button>
          <span className="text-white font-medium">{currentLabel}</span>
          <Button variant="ghost" size="sm" onClick={scanAnother} disabled={uploading}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Preview */}
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-black">
          {imageToShow && (
            <img 
              src={imageToShow} 
              alt="Preview" 
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          )}
        </div>
        
        {/* Actions */}
        <div className="p-4 border-t border-slate-700 space-y-3">
          {!croppedImage && scanType === "document" && (
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => setStep("crop")}
              disabled={uploading}
            >
              <Crop className="w-4 h-4 mr-2" />
              Recadrer le document
            </Button>
          )}
          
          <Button 
            className="w-full bg-green-600 hover:bg-green-700" 
            onClick={uploadDocument}
            disabled={uploading}
            data-testid="upload-button"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Valider et enregistrer
              </>
            )}
          </Button>
          
          <p className="text-center text-slate-400 text-xs">
            {scanType === "document" 
              ? "Le document sera converti en PDF" 
              : "La photo sera compressée et enregistrée"}
          </p>
        </div>
      </div>
    );
  }

  // ==========================================================================
  // RENDER - CROP
  // ==========================================================================
  if (step === "crop") {
    return (
      <div className="fixed inset-0 bg-slate-900 z-50 flex flex-col">
        <Toaster position="top-center" theme="dark" />
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <Button variant="ghost" size="sm" onClick={() => setStep("preview")}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Annuler
          </Button>
          <span className="text-white font-medium">Recadrer</span>
          <Button variant="ghost" size="sm" onClick={applyCrop}>
            <Check className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Zone crop */}
        <div className="flex-1 p-4 flex items-center justify-center bg-black relative overflow-hidden">
          {capturedImage && (
            <div className="relative inline-block">
              <img 
                src={capturedImage} 
                alt="À recadrer" 
                className="max-w-full max-h-[50vh] object-contain"
              />
              
              {/* Zone de crop */}
              <div 
                className="absolute border-2 border-blue-500 bg-blue-500/10 pointer-events-none"
                style={{
                  left: `${cropArea.x}%`,
                  top: `${cropArea.y}%`,
                  width: `${cropArea.width}%`,
                  height: `${cropArea.height}%`
                }}
              >
                <div className="absolute -top-2 -left-2 w-4 h-4 bg-blue-500 rounded-full" />
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-blue-500 rounded-full" />
                <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-blue-500 rounded-full" />
                <div className="absolute -bottom-2 -right-2 w-4 h-4 bg-blue-500 rounded-full" />
              </div>
            </div>
          )}
        </div>
        
        {/* Contrôles */}
        <div className="p-4 border-t border-slate-700 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-slate-400 text-xs block mb-1">Position X</label>
              <input 
                type="range" 
                min="0" 
                max={100 - cropArea.width} 
                value={cropArea.x}
                onChange={(e) => setCropArea({...cropArea, x: parseInt(e.target.value)})}
                className="w-full accent-blue-500"
              />
            </div>
            <div>
              <label className="text-slate-400 text-xs block mb-1">Position Y</label>
              <input 
                type="range" 
                min="0" 
                max={100 - cropArea.height} 
                value={cropArea.y}
                onChange={(e) => setCropArea({...cropArea, y: parseInt(e.target.value)})}
                className="w-full accent-blue-500"
              />
            </div>
            <div>
              <label className="text-slate-400 text-xs block mb-1">Largeur</label>
              <input 
                type="range" 
                min="20" 
                max={100 - cropArea.x} 
                value={cropArea.width}
                onChange={(e) => setCropArea({...cropArea, width: parseInt(e.target.value)})}
                className="w-full accent-blue-500"
              />
            </div>
            <div>
              <label className="text-slate-400 text-xs block mb-1">Hauteur</label>
              <input 
                type="range" 
                min="20" 
                max={100 - cropArea.y} 
                value={cropArea.height}
                onChange={(e) => setCropArea({...cropArea, height: parseInt(e.target.value)})}
                className="w-full accent-blue-500"
              />
            </div>
          </div>
          
          <Button className="w-full" onClick={applyCrop}>
            <Check className="w-4 h-4 mr-2" />
            Appliquer le recadrage
          </Button>
        </div>
        
        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }

  // ==========================================================================
  // RENDER - SUCCESS
  // ==========================================================================
  if (step === "success") {
    return (
      <div className="fixed inset-0 bg-slate-900 z-50 flex flex-col items-center justify-center p-6">
        <Toaster position="top-center" theme="dark" />
        
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-400" />
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-2">
            {scanType === "document" ? "Document enregistré !" : "Photo enregistrée !"}
          </h2>
          
          <p className="text-slate-400 mb-8">
            {scanType === "document"
              ? "Votre document a été ajouté au dossier."
              : "Votre photo a été ajoutée au dossier."}
          </p>
          
          <div className="space-y-3">
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={scanAnother}
            >
              <Camera className="w-4 h-4 mr-2" />
              Scanner un autre {scanType === "document" ? "document" : "photo"}
            </Button>
            
            <Button 
              variant="outline"
              className="w-full"
              onClick={handleClose}
            >
              <Home className="w-4 h-4 mr-2" />
              Terminer
            </Button>
          </div>
          
          {uploadedCount > 1 && (
            <p className="text-zinc-400 text-sm mt-6">
              {uploadedCount} éléments envoyés pendant cette session
            </p>
          )}
        </div>
      </div>
    );
  }

  return null;
}
